import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { 
  Search, 
  MapPin, 
  Star,
  ExternalLink,
  Award,
  Loader2,
  AlertTriangle,
  DollarSign
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const specialtyOptions = ['Full Swing', 'Short Game', 'Putting', 'Mental Game', 'Junior Development', "Women's Golf", 'Fitness', 'Competition Prep', 'Beginner Friendly'];

const ProsCornerPage = () => {
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');

  useEffect(() => {
    fetchPros();
  }, []);

  const fetchPros = async () => {
    try {
      const res = await axios.get(`${API_URL}/pros`);
      setPros(res.data.pros || []);
    } catch (err) {
      console.error('Failed to fetch pros:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = pros.filter(p => {
    if (search && !p.name?.toLowerCase().includes(search.toLowerCase()) && !p.bio?.toLowerCase().includes(search.toLowerCase())) return false;
    if (specialtyFilter && !p.specialties?.some(s => s.toLowerCase() === specialtyFilter.toLowerCase())) return false;
    if (stateFilter && p.state?.toUpperCase() !== stateFilter.toUpperCase()) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-bone pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-fairway" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pt-24 pb-20">
      {/* Header */}
      <div className="bg-deep-green py-12 mb-8">
        <div className="container-padding">
          <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-2">Skill Development</p>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-3" data-testid="pros-corner-title">
            Pro's Corner
          </h1>
          <p className="text-cream/70 font-body max-w-2xl">
            Find certified golf professionals and coaches. Get instruction, mentorship, and take your game to the next level.
          </p>
        </div>
      </div>

      <div className="container-padding">
        {/* Disclaimer */}
        <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-sm flex items-start gap-3" data-testid="pros-disclaimer">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-amber-800 text-xs font-body">
            <strong>Notice:</strong> Fairway Alliance provides this directory as a resource for our members. We are not responsible for, nor do we guarantee, the level of instruction, training quality, or outcomes provided by any individual listed. All sessions and agreements are strictly between the member and the professional.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search pros by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white"
              data-testid="pros-search"
            />
          </div>
          <Select value={specialtyFilter} onValueChange={(v) => setSpecialtyFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-full md:w-48 bg-white" data-testid="pros-specialty-filter">
              <SelectValue placeholder="Specialty" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Specialties</SelectItem>
              {specialtyOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={stateFilter} onValueChange={(v) => setStateFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-full md:w-40 bg-white" data-testid="pros-state-filter">
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              {[...new Set(pros.map(p => p.state))].sort().map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Pros Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {filtered.map((pro) => (
            <Card key={pro.pro_id || pro.id} className={`bg-white border-0 shadow-sm hover:shadow-md transition-all ${pro.featured ? 'ring-2 ring-gold/30' : ''}`} data-testid="pro-card">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="w-16 h-16 rounded-full bg-fairway flex items-center justify-center shrink-0">
                    <span className="text-gold font-display font-bold text-xl">
                      {pro.name?.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-display font-bold text-lg text-deep-green">{pro.name}</h3>
                      {pro.featured && <Badge className="bg-gold text-deep-green text-xs">Featured</Badge>}
                    </div>
                    <p className="text-gold text-sm font-semibold mb-1">{pro.title}</p>
                    <p className="text-muted-foreground text-sm flex items-center gap-1 mb-3">
                      <MapPin className="w-3 h-3" /> {pro.location}
                    </p>
                    
                    {/* Bio */}
                    <p className="text-sm text-foreground font-body mb-3 line-clamp-2">{pro.bio}</p>

                    {/* Specialties */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {pro.specialties?.map(s => (
                        <Badge key={s} variant="outline" className="text-xs border-gray-200">{s}</Badge>
                      ))}
                    </div>

                    {/* Certifications & Rate */}
                    <div className="flex items-center gap-4 text-sm">
                      {pro.certifications?.map(c => (
                        <span key={c} className="flex items-center gap-1 text-muted-foreground">
                          <Award className="w-3 h-3 text-gold" /> {c}
                        </span>
                      ))}
                      {pro.hourly_rate && (
                        <span className="flex items-center gap-1 text-fairway font-semibold">
                          <DollarSign className="w-3 h-3" /> ${pro.hourly_rate}/hr
                        </span>
                      )}
                    </div>

                    {/* Booking */}
                    {pro.booking_url && (
                      <a href={pro.booking_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-3">
                        <Button size="sm" className="btn-gold text-xs" data-testid="book-pro-btn">
                          Book a Session <ExternalLink className="w-3 h-3 ml-1" />
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="font-display text-lg">No professionals found matching your criteria</p>
          </div>
        )}

        {/* Listing CTA */}
        <div className="mt-12 text-center p-8 bg-white rounded-sm shadow-sm">
          <Award className="w-10 h-10 text-gold mx-auto mb-4" />
          <h3 className="font-display font-bold text-xl text-deep-green mb-2">Are You a Golf Professional?</h3>
          <p className="text-muted-foreground font-body mb-4 max-w-xl mx-auto">
            Free basic listing. Upgrade to "Featured Pro" ($5-$10/mo) for top placement and direct booking calendar integration.
          </p>
          <Button variant="outline" className="border-fairway text-fairway hover:bg-fairway hover:text-white">
            Submit Your Listing
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProsCornerPage;
